<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import axios from 'axios';
import { toast } from 'vue3-toastify';
import { useAuthStore } from '@/stores/useAuthStore';
import { backend_url, badarts_url, cmer_url } from '@/config/config';
import { normalizeForSearch } from '@/functions/utils';

const authStore = useAuthStore();

// ── Services ──────────────────────────────────────────────────────────────────

const SERVICES = {
  dartshub: { label: 'DartsHub', icon: 'fa-bullseye', baseUrl: backend_url, hasTraffic: true, hasUploads: true },
  badarts: { label: 'Badarts', icon: 'fa-trophy', baseUrl: badarts_url, hasTraffic: false, hasUploads: true },
  cmer: { label: 'CMER', icon: 'fa-shield-halved', baseUrl: cmer_url, hasTraffic: false, hasUploads: false },
};
type ServiceKey = keyof typeof SERVICES;

const selectedService = ref<ServiceKey>('dartshub');
const activeSection = ref<string>('inscriptions');

const service = computed(() => SERVICES[selectedService.value]);

const ALL_SECTIONS = [
  { key: 'inscriptions', label: 'Inscriptions', icon: 'fa-clipboard-check' },
  { key: 'registre', label: 'Registre', icon: 'fa-book' },
  { key: 'health', label: 'Santé DB', icon: 'fa-heart-pulse' },
  { key: 'logs', label: 'Logs', icon: 'fa-terminal' },
  { key: 'backups', label: 'Sauvegardes', icon: 'fa-database' },
  { key: 'traffic', label: 'Trafic', icon: 'fa-chart-line' },
  { key: 'uploads', label: 'Uploads', icon: 'fa-folder-open' },
];

const sections = computed(() => {
  const s = service.value;
  return ALL_SECTIONS.map(sec => ({
    ...sec,
    disabled: (sec.key === 'traffic' && !s.hasTraffic) || (sec.key === 'uploads' && !s.hasUploads)
      || (sec.key === 'inscriptions' && selectedService.value !== 'dartshub'),
  }));
});

// ── Authenticated API call ────────────────────────────────────────────────────

const api = async (method: string, path: string, config: Record<string, any> = {}) => {
  return axios({
    method,
    url: `${service.value.baseUrl}${path}`,
    headers: {
      Authorization: `Bearer ${authStore.token}`,
      ...config.headers,
    },
    ...config,
  });
};

// ── Health ────────────────────────────────────────────────────────────────────

interface HealthData {
  integrity_check: string;
  page_count: number;
  page_size: number;
  file_size_bytes: number;
  calculated_size_bytes: number;
  freelist_count: number;
  journal_mode: string;
  synchronous: string;
  sqlite_version: string;
}

const health = ref<HealthData | null>(null);
const tables = ref<{ table: string; rows: number }[]>([]);
const storage = ref<{ total_bytes: number; used_bytes: number; free_bytes: number } | null>(null);
const healthLoading = ref(false);

const loadHealth = async () => {
  healthLoading.value = true;
  health.value = null;
  tables.value = [];
  storage.value = null;
  try {
    const [hRes, sRes] = await Promise.all([
      api('get', '/admin/monitor/health'),
      api('get', '/admin/monitor/storage'),
    ]);
    health.value = hRes.data;
    storage.value = sRes.data;
    // Tables only available on DartsHub (full admin)
    if (selectedService.value === 'dartshub') {
      const tRes = await api('get', '/admin/monitor/tables');
      tables.value = tRes.data.tables;
    }
  } catch {
    toast.error('Erreur chargement santé DB.');
  } finally {
    healthLoading.value = false;
  }
};

const fmt = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

// ── Logs ──────────────────────────────────────────────────────────────────────

interface LogFile { filename: string; size_bytes: number; mtime: number; }

const logFiles = ref<LogFile[]>([]);
const selectedLog = ref<string>('');
const logLines = ref<string[]>([]);
const logLoading = ref(false);
const logsLoading = ref(false);
const showRotated = ref(false);
const activeLogGroup = ref<string>('app');

const LOG_GROUPS = [
  { key: 'app', label: 'App', color: 'blue' },
  { key: 'error', label: 'Erreur', color: 'red' },
  { key: 'warning', label: 'Warning', color: 'orange' },
  { key: 'debug', label: 'Debug', color: 'gray' },
];

const isCurrentLogFile = (filename: string) =>
  /^(app|error|warning|debug)\.log$/.test(filename.split(/[\\/]/).pop() || '');

const groupedFiles = computed(() => {
  const groups: Record<string, LogFile[]> = {};
  for (const f of logFiles.value) {
    const parts = f.filename.replace(/\\/g, '/').split('/');
    const group = parts.length > 1 ? parts[0] : 'app';
    if (!groups[group]) groups[group] = [];
    groups[group].push(f);
  }
  return groups;
});

const visibleFiles = computed(() => {
  const files = groupedFiles.value[activeLogGroup.value] || [];
  if (showRotated.value) return files;
  const current = files.filter(f => isCurrentLogFile(f.filename));
  return current.length ? current : files.slice(0, 1);
});

const rotatedCount = computed(() => {
  const files = groupedFiles.value[activeLogGroup.value] || [];
  return files.filter(f => !isCurrentLogFile(f.filename)).length;
});

const loadLogFiles = async () => {
  logsLoading.value = true;
  logFiles.value = [];
  selectedLog.value = '';
  logLines.value = [];
  try {
    const res = await api('get', '/admin/monitor/logs');
    logFiles.value = res.data.logs;
    // auto-select current file of active group
    const current = (groupedFiles.value[activeLogGroup.value] || []).find(f => isCurrentLogFile(f.filename));
    if (current) readLog(current.filename);
  } catch {
    toast.error('Erreur chargement logs.');
  } finally {
    logsLoading.value = false;
  }
};

const selectLogGroup = (key: string) => {
  activeLogGroup.value = key;
  showRotated.value = false;
  selectedLog.value = '';
  logLines.value = [];
  const current = (groupedFiles.value[key] || []).find(f => isCurrentLogFile(f.filename));
  if (current) readLog(current.filename);
};

const readLog = async (filename: string) => {
  selectedLog.value = filename;
  logLoading.value = true;
  logLines.value = [];
  try {
    const res = await api('get', `/admin/monitor/logs/${filename}`);
    logLines.value = res.data.lines;
  } catch {
    toast.error('Erreur lecture log.');
  } finally {
    logLoading.value = false;
  }
};

const logLineClass = (line: string) => {
  const l = line.toUpperCase();
  if (l.includes('ERROR') || l.includes('CRITICAL')) return 'log-error';
  if (l.includes('WARNING') || l.includes('WARN')) return 'log-warning';
  if (l.includes('SUCCESS')) return 'log-success';
  if (l.includes('DEBUG')) return 'log-debug';
  return 'log-info';
};

// ── Backups ───────────────────────────────────────────────────────────────────

interface Backup { filename: string; created_at: number; size_bytes: number; }

const backups = ref<Backup[]>([]);
const backupsLoading = ref(false);
const creatingBackup = ref(false);

const loadBackups = async () => {
  backupsLoading.value = true;
  try {
    const res = await api('get', '/admin/backups');
    backups.value = res.data.backups;
  } catch {
    toast.error('Erreur chargement sauvegardes.');
  } finally {
    backupsLoading.value = false;
  }
};

const createBackup = async () => {
  creatingBackup.value = true;
  try {
    const res = await api('post', '/admin/backup');
    toast.success(`Sauvegarde créée : ${res.data.filename}`);
    await loadBackups();
  } catch {
    toast.error('Erreur création sauvegarde.');
  } finally {
    creatingBackup.value = false;
  }
};

const downloadBackup = async (filename: string) => {
  try {
    const res = await api('get', `/admin/backup/${filename}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error('Erreur téléchargement.');
  }
};

const deleteBackup = async (filename: string) => {
  if (!confirm(`Supprimer ${filename} ?`)) return;
  try {
    await api('delete', `/admin/backup/${filename}`);
    backups.value = backups.value.filter(b => b.filename !== filename);
    toast.success('Sauvegarde supprimée.');
  } catch {
    toast.error('Erreur suppression.');
  }
};

const fmtDate = (ts: number) => ts ? new Date(ts * 1000).toLocaleString('fr-FR') : '-';

const fmtTs = (ts: string | number | null | undefined, timeOnly = false): string => {
  if (!ts) return '-';
  let d: Date;
  if (typeof ts === 'number') {
    d = new Date(ts * 1000);
  } else {
    const s = String(ts).replace(' ', 'T');
    d = new Date(s.includes('+') || s.endsWith('Z') ? s : s + 'Z');
  }
  if (isNaN(d.getTime())) return '-';
  return timeOnly ? d.toLocaleTimeString('fr-FR') : d.toLocaleString('fr-FR');
};

// ── Traffic ───────────────────────────────────────────────────────────────────

const trafficDays = ref(7);
const trafficData = ref<any>(null);
const sessions = ref<any[]>([]);
const trafficLoading = ref(false);
const selectedSession = ref<string>('');
const sessionDetail = ref<any[]>([]);
const sessionModal = ref(false);

const loadTraffic = async () => {
  trafficLoading.value = true;
  trafficData.value = null;
  sessions.value = [];
  try {
    const [tRes, sRes] = await Promise.all([
      api('get', '/admin/monitor/traffic', { params: { days: trafficDays.value } }),
      api('get', '/admin/monitor/sessions', { params: { days: trafficDays.value } }),
    ]);
    trafficData.value = tRes.data;
    sessions.value = sRes.data;
  } catch {
    toast.error('Erreur chargement trafic.');
  } finally {
    trafficLoading.value = false;
  }
};

const openSession = async (sessionId: string) => {
  selectedSession.value = sessionId;
  sessionModal.value = true;
  sessionDetail.value = [];
  try {
    const res = await api('get', `/admin/monitor/session/${sessionId}`);
    sessionDetail.value = res.data;
  } catch {
    toast.error('Erreur chargement session.');
  }
};

// ── Uploads ───────────────────────────────────────────────────────────────────

const uploads = ref<{ name: string; url: string; size_bytes: number }[]>([]);
const uploadsLoading = ref(false);

const loadUploads = async () => {
  uploadsLoading.value = true;
  try {
    const res = await api('get', '/admin/uploads');
    uploads.value = res.data.files;
  } catch {
    toast.error('Erreur chargement uploads.');
  } finally {
    uploadsLoading.value = false;
  }
};

// ── Registre ──────────────────────────────────────────────────────────────────

interface EventEntry {
  timestamp: string;
  type: string;
  firstname?: string | null;
  name?: string | null;
  email?: string | null;
  info?: string | null;
  // backward compat - anciens enregistrements
  nickname?: string;
  display_name?: string;
  tournamentName?: string;
  [key: string]: unknown;
}

const registreEvents = ref<EventEntry[]>([]);
const registreLoading = ref(false);
const registreSearch = ref('');

const filteredEvents = computed(() => {
  const q = normalizeForSearch(registreSearch.value);
  if (!q) return registreEvents.value;
  return registreEvents.value.filter(e =>
    normalizeForSearch(e.email).includes(q) ||
    normalizeForSearch(e.name).includes(q) ||
    normalizeForSearch(e.firstname).includes(q) ||
    normalizeForSearch(e.info).includes(q) ||
    normalizeForSearch(e.type).includes(q) ||
    normalizeForSearch(String(e.nickname || '')).includes(q) ||
    normalizeForSearch(String(e.tournamentName || '')).includes(q)
  );
});

const loadRegistre = async () => {
  registreLoading.value = true;
  registreEvents.value = [];
  try {
    const res = await api('get', '/admin/monitor/events');
    registreEvents.value = res.data.events;
  } catch {
    toast.error('Erreur chargement registre.');
  } finally {
    registreLoading.value = false;
  }
};

const eventTypeBadge = (type: string) => {
  switch (type) {
    case 'inscription': return 'badge-primary';
    case 'connexion': return 'badge-blue';
    case 'demande_mot_de_passe': return 'badge-orange';
    case 'demande_licence': return 'badge-accent';
    case 'nouvelle_licence': return 'badge-accent';
    case 'renouvellement_licence': return 'badge-accent';
    case 'transfert_licence': return 'badge-accent';
    case 'userRegister': return 'badge-orange';
    case 'paiement_inscription': return 'badge-green';
    case 'inscription_evenement': return 'badge-green';
    case 'soumission_comite': return 'badge-orange';
    case 'paiement': return 'badge-green';
    case 'userUnregister': return 'badge-red';
    case 'paiement_tpe': return 'badge-green';
    default: return 'badge-gray';
  }
};

const eventTypeLabel = (type: string) => {
  switch (type) {
    case 'inscription': return 'Création compte';
    case 'connexion': return 'Connexion';
    case 'demande_mot_de_passe': return 'Reset MDP';
    case 'demande_licence': return 'Demande licence';
    case 'nouvelle_licence': return 'Création licence';
    case 'renouvellement_licence': return 'Renouvellement';
    case 'transfert_licence': return 'Transfert licence';
    case 'userRegister': return 'Inscription tournoi';
    case 'paiement_inscription': return 'Paiement tournoi';
    case 'inscription_evenement': return 'Inscription tournoi';
    case 'soumission_comite': return 'Soumission comité';
    case 'paiement': return 'Paiement';
    case 'userUnregister': return 'Désinscription';
    case 'paiement_tpe': return 'Paiement TPE';
    default: return type;
  }
};

const eventPerson = (e: EventEntry): string => {
  if (e.firstname || e.name) return [e.firstname, e.name].filter(Boolean).join(' ');
  if (e.nickname) return e.nickname;
  if (e.display_name) return e.display_name;
  return '-';
};

const eventInfo = (e: EventEntry): string => {
  if (e.info) return e.info;
  // backward compat - anciens enregistrements sans champ info
  const ev = e as any;
  if (ev.tournamentName && ev.amount !== undefined) {
    const method = ev.method ? ` (${ev.method})` : '';
    return `${ev.tournamentName} - ${ev.amount} €${method}`;
  }
  if (ev.tournamentName) return String(ev.tournamentName);
  if (ev.licence_type || ev.club_name) {
    const type = ev.licence_type || (ev.previous_club ? 'Renouvellement' : 'Nouveau licencié');
    return ev.club_name ? `${type} - ${ev.club_name}` : type;
  }
  if (ev.club_number) return `Club ${ev.club_number}`;
  if (ev.app) return String(ev.app);
  return '-';
};

// ── Inscriptions (suivi des problèmes) ───────────────────────────────────────

type Severity = 'critical' | 'warning' | 'info';

interface InscriptionIssue {
  severity: Severity;
  category: string;
  title: string;
  at: string | null;
  detail?: string | null;
  hint?: string | null;
  players?: string;
  count?: number;
  event_id?: number;
  attempt_id?: number;
  can_recheck?: boolean;
  key?: string;
}

interface RegistrationAttemptView {
  id: number;
  at: string;
  status: string;
  email: string | null;
  name: string | null;
  event_id: number | null;
  event_name: string;
  payment_mode: string | null;
  amount_cents: number | null;
  payment_ref: string | null;
  players: string[];
  detail: string | null;
  finalized_via: string | null;
  can_recheck: boolean;
}

interface InscriptionsReport {
  hours: number;
  summary: Record<string, number>;
  issues: InscriptionIssue[];
  attempts: RegistrationAttemptView[];
}

const inscriptionsHours = ref(48);
const inscriptionsReport = ref<InscriptionsReport | null>(null);
const inscriptionsLoading = ref(false);
const severityFilter = ref<Severity | 'all'>('all');
const rechecking = ref<number | null>(null);

const SEVERITY_META: Record<Severity, { label: string; icon: string; badge: string }> = {
  critical: { label: 'Critique', icon: 'fa-circle-exclamation', badge: 'badge-red' },
  warning: { label: 'À vérifier', icon: 'fa-triangle-exclamation', badge: 'badge-orange' },
  info: { label: 'Info', icon: 'fa-circle-info', badge: 'badge-gray' },
};

const ATTEMPT_STATUS: Record<string, { label: string; badge: string }> = {
  finalized: { label: 'Inscrit', badge: 'badge-green' },
  initiated: { label: 'Paiement en cours', badge: 'badge-blue' },
  unconfirmed: { label: 'Non confirmé', badge: 'badge-orange' },
  abandoned: { label: 'Abandonné', badge: 'badge-gray' },
  rejected: { label: 'Refusé', badge: 'badge-gray' },
  error: { label: 'Erreur', badge: 'badge-red' },
};

const PAYMENT_MODE_LABEL: Record<string, string> = {
  online: 'En ligne', onsite: 'Sur place', demo: 'Démo',
};
const VIA_LABEL: Record<string, string> = {
  webhook: 'webhook HelloAsso', polling: 'retour du joueur', direct: 'immédiat', admin: 'vérif. admin',
};

const filteredIssues = computed(() => {
  const issues = inscriptionsReport.value?.issues || [];
  return severityFilter.value === 'all' ? issues : issues.filter(i => i.severity === severityFilter.value);
});

const problemCount = computed(() => {
  const s = inscriptionsReport.value?.summary;
  return s ? s.critical + s.warning : 0;
});

const periodLabel = computed(() => {
  const h = inscriptionsHours.value;
  return h < 48 ? `${h} dernières heures` : `${Math.round(h / 24)} derniers jours`;
});

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

const verdict = computed(() => {
  const s = inscriptionsReport.value?.summary;
  if (s?.critical) {
    return {
      cls: 'is-critical', icon: 'fa-circle-exclamation',
      title: `${plural(s.critical, 'problème critique', 'problèmes critiques')} sur les ${periodLabel.value}`,
    };
  }
  if (s?.warning) {
    return {
      cls: 'is-warning', icon: 'fa-triangle-exclamation',
      title: `${plural(s.warning, 'point à vérifier', 'points à vérifier')} sur les ${periodLabel.value}`,
    };
  }
  return { cls: 'is-ok', icon: 'fa-circle-check', title: `Aucun problème d'inscription sur les ${periodLabel.value}` };
});

const summaryTiles = computed(() => {
  const s = inscriptionsReport.value?.summary || {};
  return [
    { label: 'Tentatives', value: s.attempts ?? 0 },
    { label: 'Inscrits', value: s.finalized ?? 0, color: '--success' },
    { label: 'Paiements en cours', value: s.pending ?? 0 },
    { label: 'Non confirmés', value: s.unconfirmed ?? 0, color: '--warning' },
    { label: 'Abandonnés', value: s.abandoned ?? 0 },
    { label: 'Refusés', value: s.rejected ?? 0 },
    { label: 'Erreurs', value: s.errors ?? 0, color: '--danger' },
  ];
});

const attemptMode = (a: RegistrationAttemptView) => {
  const mode = PAYMENT_MODE_LABEL[a.payment_mode || ''] || a.payment_mode || '';
  const via = a.status === 'finalized' && a.finalized_via ? VIA_LABEL[a.finalized_via] || a.finalized_via : '';
  return [mode, via].filter(Boolean).join(' · ');
};

const euros = (cents: number | null | undefined) =>
  cents == null ? '-' : `${(cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;

const loadInscriptions = async () => {
  inscriptionsLoading.value = true;
  try {
    const res = await api('get', '/admin/monitor/inscriptions', { params: { hours: inscriptionsHours.value } });
    inscriptionsReport.value = res.data;
  } catch {
    toast.error('Erreur chargement du suivi des inscriptions.');
  } finally {
    inscriptionsLoading.value = false;
  }
};

const acknowledging = ref<string | null>(null);

const acknowledgeIssue = async (key: string) => {
  acknowledging.value = key;
  try {
    await api('post', '/admin/monitor/inscriptions/acknowledge', { data: { key } });
    toast.success('Problème marqué comme traité.');
    await loadInscriptions();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Impossible de marquer ce problème comme traité.');
  } finally {
    acknowledging.value = null;
  }
};

const recheckAttempt = async (attemptId: number) => {
  rechecking.value = attemptId;
  try {
    const res = await api('post', `/admin/monitor/inscriptions/attempts/${attemptId}/recheck`);
    if (res.data.status === 'finalized') toast.success(res.data.message);
    else toast.info(res.data.message);
    await loadInscriptions();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Vérification HelloAsso impossible.');
  } finally {
    rechecking.value = null;
  }
};

// ── Navigation ────────────────────────────────────────────────────────────────

const defaultSection = (key: ServiceKey) => (key === 'dartshub' ? 'inscriptions' : 'registre');

const selectService = (key: ServiceKey) => {
  selectedService.value = key;
  activeSection.value = defaultSection(key);
};

const selectSection = (key: string) => {
  const sec = sections.value.find(s => s.key === key);
  if (sec?.disabled) return;
  activeSection.value = key;
  if (key === 'inscriptions') loadInscriptions();
  if (key === 'registre') loadRegistre();
  if (key === 'health') loadHealth();
  if (key === 'logs') loadLogFiles();
  if (key === 'backups') loadBackups();
  if (key === 'traffic') loadTraffic();
  if (key === 'uploads') loadUploads();
};

watch(selectedService, (key) => {
  activeSection.value = defaultSection(key);
  health.value = null;
  storage.value = null;
  tables.value = [];
  backups.value = [];
  logFiles.value = [];
  logLines.value = [];
  uploads.value = [];
  registreEvents.value = [];
  if (activeSection.value === 'inscriptions') loadInscriptions();
  else loadRegistre();
});

onMounted(() => loadInscriptions());
</script>

<template>
  <div class="page">
    <div class="page-header">
      <h1><i class="fa-solid fa-gauge-high" /> Monitoring</h1>
    </div>

    <!-- Service selector -->
    <div class="service-tabs">
      <button v-for="(svc, key) in SERVICES" class="service-tab" :class="{ active: selectedService === key }" @click="selectService(key as ServiceKey)"
        :key="key">
        <i :class="`fa-solid ${svc.icon}`" />
        {{ svc.label }}
      </button>
    </div>

    <!-- Section tabs -->
    <div class="section-tabs">
      <button v-for="sec in sections" class="section-tab" :class="{ active: activeSection === sec.key, disabled: sec.disabled }"
        :title="sec.disabled ? 'Non disponible pour ce service' : undefined"
        @click="selectSection(sec.key)" :key="sec.key">
        <i :class="`fa-solid ${sec.icon}`" />
        {{ sec.label }}
        <span v-if="sec.key === 'inscriptions' && problemCount > 0" class="tab-alert">{{ problemCount }}</span>
      </button>
    </div>

    <!-- ── Inscriptions ─────────────────────────────────────────────────────── -->
    <div v-if="activeSection === 'inscriptions'">
      <div class="section-actions">
        <div style="display:flex;align-items:center;gap:0.5rem">
          <label style="font-size:0.875rem;color:var(--text-muted)">Période :</label>
          <select v-model="inscriptionsHours" class="select-sm" @change="loadInscriptions">
            <option :value="24">24h</option>
            <option :value="48">48h</option>
            <option :value="168">7 jours</option>
            <option :value="720">30 jours</option>
          </select>
        </div>
        <button class="btn btn-ghost btn-sm" :disabled="inscriptionsLoading" @click="loadInscriptions">
          <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': inscriptionsLoading }" /> Actualiser
        </button>
      </div>

      <div v-if="inscriptionsLoading && !inscriptionsReport" class="loader">
        <i class="fa-solid fa-spinner fa-spin" /> Analyse des inscriptions…
      </div>
      <div v-else-if="inscriptionsReport">
        <!-- Verdict -->
        <div class="ins-verdict" :class="verdict.cls">
          <i class="fa-solid" :class="verdict.icon" />
          <div>
            <div class="ins-verdict-title">{{ verdict.title }}</div>
            <div class="ins-verdict-sub">
              {{ plural(inscriptionsReport.summary.finalized, 'inscription finalisée', 'inscriptions finalisées') }}
              · {{ euros(inscriptionsReport.summary.online_paid_cents) }} encaissés en ligne
              · {{ plural(inscriptionsReport.summary.inscriptions_created, 'ligne créée', 'lignes créées') }}
              (imports et ajouts manuels compris)
            </div>
          </div>
        </div>

        <!-- Chiffres -->
        <div class="stats-row ins-stats">
          <div v-for="tile in summaryTiles" class="stat-card" :key="tile.label">
            <div class="stat-num" :style="tile.color && tile.value ? `color:var(${tile.color})` : ''">{{ tile.value }}</div>
            <div class="stat-label">{{ tile.label }}</div>
          </div>
        </div>

        <!-- Problèmes -->
        <div class="card" style="margin-top:1.25rem">
          <div class="ins-card-head">
            <div class="card-title" style="margin:0"><i class="fa-solid fa-list-check" /> Problèmes détectés</div>
            <div class="ins-filters">
              <button class="ins-filter" :class="{ active: severityFilter === 'all' }" @click="severityFilter = 'all'">
                Tout ({{ inscriptionsReport.issues.length }})
              </button>
              <button v-for="(meta, sev) in SEVERITY_META" class="ins-filter" :class="{ active: severityFilter === sev }"
                @click="severityFilter = sev" :key="sev">
                {{ meta.label }} ({{ inscriptionsReport.summary[sev] }})
              </button>
            </div>
          </div>

          <div v-if="!filteredIssues.length" class="empty">
            {{ inscriptionsReport.issues.length ? 'Aucun problème de ce niveau.' : 'Rien à signaler.' }}
          </div>
          <div v-if="inscriptionsReport.summary.acknowledged_hidden" class="ins-acked">
            <i class="fa-solid fa-eye-slash" />
            {{ plural(inscriptionsReport.summary.acknowledged_hidden, 'problème déjà traité masqué', 'problèmes déjà traités masqués') }}
            (il réapparaîtra s'il se reproduit).
          </div>
          <div v-else class="ins-issues">
            <div v-for="(issue, i) in filteredIssues" class="ins-issue" :class="`sev-${issue.severity}`" :key="i">
              <div class="ins-issue-top">
                <span class="badge" :class="SEVERITY_META[issue.severity].badge">
                  <i class="fa-solid" :class="SEVERITY_META[issue.severity].icon" /> {{ issue.category }}
                </span>
                <span v-if="issue.count && issue.count > 1" class="badge badge-gray">× {{ issue.count }}</span>
                <span class="ins-issue-date">{{ fmtTs(issue.at) }}</span>
              </div>
              <div class="ins-issue-title">{{ issue.title }}</div>
              <div v-if="issue.players" class="ins-issue-line"><i class="fa-solid fa-user" /> {{ issue.players }}</div>
              <div v-if="issue.detail" class="ins-issue-detail">{{ issue.detail }}</div>
              <div class="ins-issue-foot">
                <span class="ins-issue-hint">
                  <template v-if="issue.hint"><i class="fa-regular fa-lightbulb" /> {{ issue.hint }}</template>
                </span>
                <div class="ins-issue-actions">
                  <button v-if="issue.can_recheck && issue.attempt_id" class="btn btn-primary btn-sm"
                    :disabled="rechecking === issue.attempt_id" @click="recheckAttempt(issue.attempt_id)">
                    <i class="fa-solid" :class="rechecking === issue.attempt_id ? 'fa-spinner fa-spin' : 'fa-magnifying-glass-dollar'" />
                    Vérifier chez HelloAsso
                  </button>
                  <button v-if="issue.key" class="btn btn-ghost btn-sm" :disabled="acknowledging === issue.key"
                    title="Masquer ce problème tant qu'il ne se reproduit pas" @click="acknowledgeIssue(issue.key)">
                    <i class="fa-solid" :class="acknowledging === issue.key ? 'fa-spinner fa-spin' : 'fa-check'" />
                    Marquer comme traité
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Fil des tentatives -->
        <div class="card" style="margin-top:1.25rem">
          <div class="card-title"><i class="fa-solid fa-timeline" /> Dernières tentatives d'inscription</div>
          <div v-if="!inscriptionsReport.attempts.length" class="empty">
            Aucune tentative sur la période (le suivi démarre au déploiement de cette fonctionnalité).
          </div>
          <template v-else>
            <div class="table-wrapper" style="margin-top:0">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Statut</th>
                    <th>Qui</th>
                    <th>Tournoi</th>
                    <th>Joueurs</th>
                    <th style="text-align:right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="a in inscriptionsReport.attempts" :key="a.id">
                    <td style="font-size:0.8rem;white-space:nowrap">{{ fmtTs(a.at) }}</td>
                    <td>
                      <span class="badge" :class="ATTEMPT_STATUS[a.status]?.badge || 'badge-gray'">
                        {{ ATTEMPT_STATUS[a.status]?.label || a.status }}
                      </span>
                      <div v-if="a.detail && a.status !== 'finalized'" class="ins-attempt-detail">{{ a.detail }}</div>
                    </td>
                    <td>
                      <div>{{ a.name || '-' }}</div>
                      <div class="email-cell">{{ a.email }}</div>
                    </td>
                    <td style="font-size:0.85rem">{{ a.event_name }}</td>
                    <td style="font-size:0.82rem;color:var(--text-muted)">
                      <div v-for="p in a.players" :key="p">{{ p }}</div>
                    </td>
                    <td style="text-align:right;white-space:nowrap">
                      <div>{{ euros(a.amount_cents) }}</div>
                      <div style="font-size:0.75rem;color:var(--text-muted)">{{ attemptMode(a) }}</div>
                      <div v-if="a.payment_ref" style="font-size:0.75rem;color:var(--text-muted)" title="Code visible sur HelloAsso">#{{ a.payment_ref }}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="mobile-cards" style="margin-top:0.75rem">
              <div v-for="a in inscriptionsReport.attempts" class="mobile-card" :key="a.id">
                <div class="card-top">
                  <span style="font-size:0.78rem;color:var(--text-muted)">{{ fmtTs(a.at) }}</span>
                  <span class="badge" :class="ATTEMPT_STATUS[a.status]?.badge || 'badge-gray'">
                    {{ ATTEMPT_STATUS[a.status]?.label || a.status }}
                  </span>
                </div>
                <div style="font-weight:500;font-size:0.9rem">{{ a.name || a.email }} · {{ euros(a.amount_cents) }}<span v-if="a.payment_ref"> · #{{ a.payment_ref }}</span></div>
                <div style="font-size:0.82rem">{{ a.event_name }}</div>
                <div v-if="a.players.length" style="font-size:0.8rem;color:var(--text-muted)">{{ a.players.join(' · ') }}</div>
                <div v-if="a.detail && a.status !== 'finalized'" class="ins-attempt-detail">{{ a.detail }}</div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- ── Registre ────────────────────────────────────────────────────────── -->
    <div v-if="activeSection === 'registre'">
      <div class="section-actions">
        <div class="search-bar">
          <i class="fa-solid fa-magnifying-glass" style="color:var(--text-muted)" />
          <input v-model="registreSearch" placeholder="Rechercher…" />
        </div>
        <button class="btn btn-ghost btn-sm" @click="loadRegistre">
          <i class="fa-solid fa-rotate-right" /> Actualiser
        </button>
      </div>
      <div v-if="registreLoading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>
      <div v-else-if="filteredEvents.length === 0" class="empty">
        {{ registreEvents.length === 0 ? 'Aucun événement enregistré.' : 'Aucun résultat pour cette recherche.' }}
      </div>
      <div v-else class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Personne</th>
              <th>Email</th>
              <th>Infos</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(evt, i) in filteredEvents" :key="i">
              <td style="font-size:0.8rem;white-space:nowrap">{{ fmtTs(evt.timestamp) }}</td>
              <td>
                <span class="badge" :class="eventTypeBadge(evt.type)">{{ eventTypeLabel(evt.type) }}</span>
              </td>
              <td>{{ eventPerson(evt) }}</td>
              <td class="email-cell">{{ evt.email || '-' }}</td>
              <td style="color:var(--text-muted);font-size:0.82rem">{{ eventInfo(evt) }}</td>
            </tr>
          </tbody>
        </table>
        <div class="table-count">{{ filteredEvents.length }} / {{ registreEvents.length }} événements</div>
      </div>

      <div v-if="!registreLoading && filteredEvents.length > 0" class="mobile-cards">
        <div v-for="(evt, i) in filteredEvents" class="mobile-card" :key="i">
          <div class="card-top">
            <span style="font-size:0.78rem;color:var(--text-muted)">{{ fmtTs(evt.timestamp) }}</span>
            <span class="badge" :class="eventTypeBadge(evt.type)">{{ eventTypeLabel(evt.type) }}</span>
          </div>
          <div style="font-weight:500;font-size:0.9rem">{{ eventPerson(evt) }}</div>
          <div v-if="evt.email" class="email-cell">{{ evt.email }}</div>
          <div v-if="eventInfo(evt) !== '-'" style="font-size:0.8rem;color:var(--text-muted)">{{ eventInfo(evt) }}</div>
        </div>
        <div class="table-count">{{ filteredEvents.length }} / {{ registreEvents.length }} événements</div>
      </div>
    </div>

    <!-- ── Health ──────────────────────────────────────────────────────────── -->
    <div v-if="activeSection === 'health'">
      <div v-if="healthLoading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>
      <div v-else-if="health" class="cards-grid">
        <!-- DB Status -->
        <div class="card">
          <div class="card-title"><i class="fa-solid fa-circle-check" /> Intégrité</div>
          <div class="stat-row">
            <span class="stat-label">Intégrité</span>
            <span class="badge" :class="health.integrity_check === 'ok' ? 'badge-green' : 'badge-red'">
              {{ health.integrity_check }}
            </span>
          </div>
          <div class="stat-row">
            <span class="stat-label">Journal mode</span>
            <span class="badge badge-blue">{{ health.journal_mode }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">Synchronous</span>
            <span class="stat-val">{{ health.synchronous }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">SQLite version</span>
            <span class="stat-val">{{ health.sqlite_version }}</span>
          </div>
        </div>

        <!-- DB Size -->
        <div class="card">
          <div class="card-title"><i class="fa-solid fa-hard-drive" /> Taille DB</div>
          <div class="stat-row">
            <span class="stat-label">Taille fichier</span>
            <span class="stat-val">{{ fmt(health.file_size_bytes) }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">Taille calculée</span>
            <span class="stat-val">{{ fmt(health.calculated_size_bytes) }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">Pages</span>
            <span class="stat-val">{{ health.page_count.toLocaleString() }} × {{ fmt(health.page_size) }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">Pages libres</span>
            <span class="stat-val">{{ health.freelist_count }}</span>
          </div>
        </div>

        <!-- Storage -->
        <div v-if="storage" class="card">
          <div class="card-title"><i class="fa-solid fa-server" /> Disque</div>
          <div class="stat-row">
            <span class="stat-label">Total</span>
            <span class="stat-val">{{ fmt(storage.total_bytes) }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">Utilisé</span>
            <span class="stat-val">{{ fmt(storage.used_bytes) }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">Libre</span>
            <span class="stat-val">{{ fmt(storage.free_bytes) }}</span>
          </div>
          <div class="disk-bar">
            <div class="disk-fill"
              :style="{ width: `${(storage.used_bytes / storage.total_bytes * 100).toFixed(1)}%` }" />
          </div>
          <div class="stat-val" style="text-align:right;font-size:0.75rem;margin-top:0.25rem">
            {{ (storage.used_bytes / storage.total_bytes * 100).toFixed(1) }}% utilisé
          </div>
        </div>

        <!-- Tables (FD only) -->
        <div v-if="tables.length" class="card card-wide">
          <div class="card-title"><i class="fa-solid fa-table" /> Tables</div>
          <table>
            <thead>
              <tr>
                <th>Table</th>
                <th style="text-align:right">Lignes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="t in tables" :key="t.table">
                <td>{{ t.table }}</td>
                <td style="text-align:right;font-variant-numeric:tabular-nums">{{ t.rows?.toLocaleString() ?? '-' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ── Logs ─────────────────────────────────────────────────────────────── -->
    <div v-if="activeSection === 'logs'">
      <div v-if="logsLoading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>
      <div v-else-if="logFiles.length === 0" class="empty">Aucun fichier de log trouvé.</div>
      <div v-else class="logs-layout">
        <!-- Sidebar -->
        <div class="logs-sidebar">
          <!-- Group tabs -->
          <div class="log-groups">
            <button v-for="g in LOG_GROUPS" class="log-group-btn" :class="[`log-group-${g.color}`, { active: activeLogGroup === g.key }]"
              @click="selectLogGroup(g.key)" :key="g.key">
              {{ g.label }}
              <span v-if="groupedFiles[g.key]?.length" class="log-group-count">{{ groupedFiles[g.key].length }}</span>
            </button>
          </div>

          <!-- File list -->
          <div class="log-file-list">
            <button v-for="f in visibleFiles" class="log-file-btn" :class="{ active: selectedLog === f.filename, current: isCurrentLogFile(f.filename) }"
              @click="readLog(f.filename)"
              :key="f.filename">
              <div class="log-file-top">
                <i class="fa-solid fa-file-lines" />
                <span class="log-file-name">{{ f.filename.split(/[\\/]/).pop() }}</span>
                <span class="log-file-size">{{ fmt(f.size_bytes) }}</span>
              </div>
              <div class="log-file-date">{{ fmtTs(f.mtime) }}</div>
            </button>

            <button v-if="rotatedCount > 0" class="log-rotated-toggle" @click="showRotated = !showRotated">
              <i :class="`fa-solid fa-chevron-${showRotated ? 'up' : 'down'}`" />
              {{ showRotated ? 'Masquer les anciens' : `${rotatedCount} ancien(s)` }}
            </button>
          </div>
        </div>

        <!-- Log content -->
        <div class="logs-content">
          <div v-if="!selectedLog" class="empty-state">
            <i class="fa-solid fa-file-lines" style="font-size:2rem;opacity:0.3" />
            <p>Sélectionner un fichier</p>
          </div>
          <div v-else-if="logLoading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Lecture…</div>
          <div v-else class="log-viewer">
            <div class="log-viewer-header">
              <span class="log-viewer-filename"><i class="fa-solid fa-file-lines" /> {{ selectedLog }}</span>
              <span class="log-viewer-count">{{ logLines.length }} lignes</span>
            </div>
            <div class="log-lines">
              <div v-for="(line, i) in logLines" class="log-line" :class="logLineClass(line)" :key="i">{{ line }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Backups ───────────────────────────────────────────────────────────── -->
    <div v-if="activeSection === 'backups'">
      <div class="section-actions">
        <button class="btn btn-primary btn-sm" :disabled="creatingBackup" @click="createBackup">
          <i v-if="creatingBackup" class="fa-solid fa-spinner fa-spin" />
          <i v-else class="fa-solid fa-database" />
          Créer une sauvegarde
        </button>
      </div>
      <div v-if="backupsLoading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>
      <div v-else-if="backups.length === 0" class="empty">Aucune sauvegarde</div>
      <div v-else class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Fichier</th>
              <th>Date</th>
              <th>Taille</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="b in backups" :key="b.filename">
              <td style="font-family:monospace;font-size:0.8rem">{{ b.filename }}</td>
              <td>{{ fmtDate(b.created_at) }}</td>
              <td>{{ fmt(b.size_bytes) }}</td>
              <td>
                <div class="action-btns">
                  <button class="btn btn-ghost btn-sm" title="Télécharger" @click="downloadBackup(b.filename)">
                    <i class="fa-solid fa-download" />
                  </button>
                  <button class="btn btn-danger btn-sm" title="Supprimer" @click="deleteBackup(b.filename)">
                    <i class="fa-solid fa-trash" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!backupsLoading && backups.length > 0" class="mobile-cards">
        <div v-for="b in backups" class="mobile-card" :key="b.filename">
          <div style="font-family:monospace;font-size:0.78rem;word-break:break-all">{{ b.filename }}</div>
          <div style="font-size:0.82rem;color:var(--text-muted)">{{ fmtDate(b.created_at) }} · {{ fmt(b.size_bytes) }}
          </div>
          <div class="action-btns">
            <button class="btn btn-ghost btn-sm" title="Télécharger" @click="downloadBackup(b.filename)">
              <i class="fa-solid fa-download" /> Télécharger
            </button>
            <button class="btn btn-danger btn-sm" title="Supprimer" @click="deleteBackup(b.filename)">
              <i class="fa-solid fa-trash" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Traffic ──────────────────────────────────────────────────────────── -->
    <div v-if="activeSection === 'traffic'">
      <div class="section-actions">
        <div style="display:flex;align-items:center;gap:0.5rem">
          <label style="font-size:0.875rem;color:var(--text-muted)">Période :</label>
          <select v-model="trafficDays" class="select-sm" @change="loadTraffic">
            <option :value="1">24h</option>
            <option :value="7">7 jours</option>
            <option :value="30">30 jours</option>
            <option :value="90">90 jours</option>
          </select>
        </div>
      </div>

      <div v-if="trafficLoading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>
      <div v-else-if="trafficData">
        <!-- Stats cards -->
        <div class="stats-row">
          <div class="stat-card">
            <div class="stat-num">{{ trafficData.unique_users }}</div>
            <div class="stat-label">Utilisateurs uniques</div>
          </div>
          <div class="stat-card">
            <div class="stat-num">{{ trafficData.total_page_views }}</div>
            <div class="stat-label">Pages vues</div>
          </div>
          <div class="stat-card">
            <div class="stat-num">{{ trafficData.unique_sessions }}</div>
            <div class="stat-label">Sessions</div>
          </div>
        </div>

        <div class="traffic-grid">
          <!-- Popular pages -->
          <div class="card">
            <div class="card-title"><i class="fa-solid fa-star" /> Pages populaires</div>
            <div v-if="!trafficData.popular_pages.length" class="empty">Aucune donnée</div>
            <div v-for="p in trafficData.popular_pages" class="stat-row" :key="p.page">
              <span class="stat-label" style="font-family:monospace;font-size:0.8rem">{{ p.page }}</span>
              <span class="badge badge-blue">{{ p.views }}</span>
            </div>
          </div>

          <!-- Active users -->
          <div class="card">
            <div class="card-title"><i class="fa-solid fa-users" /> Utilisateurs actifs</div>
            <div v-if="!trafficData.active_users.length" class="empty">Aucune donnée</div>
            <div v-for="u in trafficData.active_users" class="stat-row" :key="u.user_id">
              <span class="stat-label">{{ u.user_id }}</span>
              <span class="badge badge-gray">{{ u.views }} vues</span>
            </div>
          </div>
        </div>

        <!-- Sessions table -->
        <div class="card" style="margin-top:1.25rem">
          <div class="card-title"><i class="fa-solid fa-clock-rotate-left" /> Sessions récentes</div>
          <div v-if="!sessions.length" class="empty">Aucune session</div>
          <div v-else class="table-wrapper" style="margin-top:0">
            <table>
              <thead>
                <tr>
                  <th>Session</th>
                  <th>Utilisateur</th>
                  <th>Pages</th>
                  <th>Début</th>
                  <th>Référent</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in sessions" :key="s.session_id">
                  <td style="font-family:monospace;font-size:0.75rem">{{ s.session_id.substring(0, 12) }}…</td>
                  <td>{{ s.user_id }}</td>
                  <td>{{ s.page_count }}</td>
                  <td style="font-size:0.8rem">{{ fmtTs(s.first_timestamp) }}</td>
                  <td style="font-size:0.8rem;color:var(--text-muted)">{{ s.referrer }}</td>
                  <td>
                    <button class="btn btn-ghost btn-sm" @click="openSession(s.session_id)">
                      <i class="fa-solid fa-eye" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="sessions.length > 0" class="mobile-cards" style="margin-top:0.75rem">
            <div v-for="s in sessions" class="mobile-card" :key="s.session_id">
              <div class="card-top">
                <span style="font-family:monospace;font-size:0.75rem">{{ s.session_id.substring(0, 12) }}…</span>
                <button class="btn btn-ghost btn-sm" @click="openSession(s.session_id)">
                  <i class="fa-solid fa-eye" />
                </button>
              </div>
              <div style="font-size:0.85rem">{{ s.user_id }} · {{ s.page_count }} pages</div>
              <div style="font-size:0.8rem;color:var(--text-muted)">{{ fmtTs(s.first_timestamp) }}</div>
              <div v-if="s.referrer" style="font-size:0.78rem;color:var(--text-muted)">{{ s.referrer }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Uploads ──────────────────────────────────────────────────────────── -->
    <div v-if="activeSection === 'uploads'">
      <div v-if="uploadsLoading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>
      <div v-else-if="uploads.length === 0" class="empty">Aucun fichier</div>
      <div v-else class="uploads-grid">
        <div v-for="f in uploads" class="upload-card" :key="f.name">
          <div class="upload-icon">
            <i v-if="/\.(png|jpg|jpeg|gif|webp|svg)$/i.test(f.name)" class="fa-solid fa-image" />
            <i v-else class="fa-solid fa-file" />
          </div>
          <div class="upload-info">
            <div class="upload-name">{{ f.name }}</div>
            <div class="upload-size">{{ fmt(f.size_bytes) }}</div>
          </div>
          <a v-if="f.url" :href="f.url" target="_blank" class="btn btn-ghost btn-sm" title="Ouvrir">
            <i class="fa-solid fa-external-link" />
          </a>
        </div>
      </div>
    </div>

    <!-- ── Session detail modal ──────────────────────────────────────────────── -->
    <div v-if="sessionModal" class="modal-overlay" @click.self="sessionModal = false">
      <div class="modal">
        <div class="modal-header">
          <h2><i class="fa-solid fa-clock-rotate-left" /> Session</h2>
          <button class="btn btn-ghost btn-sm" @click="sessionModal = false"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="session-id">{{ selectedSession }}</div>
        <div v-if="!sessionDetail.length" style="color:var(--text-muted);font-size:0.875rem">Chargement…</div>
        <div v-else class="session-pages">
          <div v-for="(p, i) in sessionDetail" class="session-page" :key="i">
            <span class="session-time">{{ fmtTs(p.timestamp, true) }}</span>
            <span class="session-path">{{ p.page_path }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Service tabs ─── */
.service-tabs {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.service-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg-card);
  color: var(--text-muted);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.service-tab:hover {
  color: var(--text);
  background: var(--bg-input);
}

.service-tab.active {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}

/* ── Section tabs ─── */
.section-tabs {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  width: 100%;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--border);
}

.section-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.5rem 0.25rem;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-muted);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.section-tab:hover {
  color: var(--text);
}

.section-tab.active {
  color: var(--primary);
  border-bottom-color: var(--primary);
}

.section-tab.disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.section-tab.disabled:hover {
  color: var(--text-muted);
}

/* ── Cards ─── */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.25rem;
}

.card-wide {
  grid-column: 1 / -1;
}

.card-title {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
  border-bottom: 1px solid var(--border);
}

.stat-row:last-child {
  border-bottom: none;
}

.stat-label {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.stat-val {
  font-size: 0.85rem;
  font-weight: 500;
}

.disk-bar {
  height: 6px;
  background: var(--bg-input);
  border-radius: 3px;
  margin-top: 0.75rem;
  overflow: hidden;
}

.disk-fill {
  height: 100%;
  background: var(--primary);
  border-radius: 3px;
  transition: width 0.4s;
}

/* ── Logs ─── */
.logs-layout {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.logs-sidebar {
  width: 230px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: sticky;
  top: 1rem;
  max-height: calc(100vh - 8rem);
  overflow-y: auto;
}

.log-groups {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.log-group-btn {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.65rem;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--bg-card);
  color: var(--text-muted);
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.log-group-btn:hover {
  color: var(--text);
  background: var(--bg-input);
}

.log-group-btn.active.log-group-blue {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #fff;
}

.log-group-btn.active.log-group-red {
  background: #ef4444;
  border-color: #ef4444;
  color: #fff;
}

.log-group-btn.active.log-group-orange {
  background: #f59e0b;
  border-color: #f59e0b;
  color: #fff;
}

.log-group-btn.active.log-group-gray {
  background: var(--text-muted);
  border-color: var(--text-muted);
  color: #fff;
}

.log-group-count {
  font-size: 0.7rem;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 10px;
  padding: 0 0.35rem;
}

.log-file-list {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1;
  overflow-y: auto;
}

.log-file-btn {
  padding: 0.5rem 0.6rem;
  border-radius: 6px;
  border: none;
  background: none;
  color: var(--text-muted);
  font-size: 0.78rem;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  width: 100%;
}

.log-file-btn:hover {
  background: var(--bg-input);
  color: var(--text);
}

.log-file-btn.active {
  background: var(--primary);
  color: #fff;
}

.log-file-btn.current .log-file-name {
  font-weight: 600;
  color: var(--text);
}

.log-file-btn.active .log-file-name {
  color: #fff;
}

.log-file-top {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.log-file-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.log-file-size {
  font-size: 0.7rem;
  opacity: 0.6;
  flex-shrink: 0;
}

.log-file-date {
  font-size: 0.68rem;
  color: var(--text-muted);
  margin-top: 0.15rem;
  padding-left: 1.25rem;
  opacity: 0.7;
}

.log-file-btn.active .log-file-date {
  color: rgba(255, 255, 255, 0.7);
}

.log-rotated-toggle {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  border: 1px dashed var(--border);
  background: none;
  color: var(--text-muted);
  font-size: 0.75rem;
  cursor: pointer;
  width: 100%;
  margin-top: 0.25rem;
  transition: all 0.15s;
}

.log-rotated-toggle:hover {
  color: var(--text);
  border-color: var(--text-muted);
}

.logs-content {
  flex: 1;
  min-width: 0;
}

.log-viewer {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  min-height: 500px;
  display: flex;
  flex-direction: column;
}

.log-viewer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 1rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-input);
}

.log-viewer-filename {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.log-viewer-count {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.log-lines {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.75rem;
  line-height: 1.6;
}

.log-line {
  padding: 0.1rem 0;
  white-space: pre-wrap;
  word-break: break-all;
}

.log-error {
  color: #f87171;
}

.log-warning {
  color: #fbbf24;
}

.log-success {
  color: #34d399;
}

.log-debug {
  color: var(--text-muted);
  opacity: 0.7;
}

.log-info {
  color: var(--text);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: var(--text-muted);
  gap: 0.5rem;
}

/* ── Backups ─── */
.section-actions {
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.action-btns {
  display: flex;
  gap: 0.4rem;
}

/* ── Traffic ─── */
.stats-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.stat-card {
  flex: 1;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.25rem;
  text-align: center;
}

.stat-num {
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary);
}

.stat-card .stat-label {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
}

.traffic-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.select-sm {
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--bg-input);
  color: var(--text);
  font-size: 0.875rem;
}

/* ── Uploads ─── */
.uploads-grid {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.upload-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.upload-icon {
  color: var(--primary);
  font-size: 1.25rem;
  width: 28px;
  text-align: center;
}

.upload-info {
  flex: 1;
}

.upload-name {
  font-size: 0.875rem;
  font-weight: 500;
}

.upload-size {
  font-size: 0.75rem;
  color: var(--text-muted);
}

/* ── Session modal ─── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.modal {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem;
  width: 90%;
  max-width: 520px;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.modal-header h2 {
  font-size: 1rem;
  font-weight: 600;
}

.session-id {
  font-family: monospace;
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-bottom: 1rem;
  word-break: break-all;
}

.session-pages {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.session-page {
  display: flex;
  gap: 0.75rem;
  padding: 0.35rem 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.82rem;
}

.session-page:last-child {
  border-bottom: none;
}

.session-time {
  color: var(--text-muted);
  flex-shrink: 0;
}

.session-path {
  font-family: monospace;
}

/* ── Registre ─── */
.search-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.35rem 0.75rem;
  flex: 1;
  max-width: 320px;
}

.search-bar input {
  background: none;
  border: none;
  outline: none;
  color: var(--text);
  font-size: 0.875rem;
  width: 100%;
}

.email-cell {
  color: var(--text-muted);
  font-size: 0.8rem;
}

.tab-alert {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.15rem;
  height: 1.15rem;
  padding: 0 0.3rem;
  margin-left: 0.25rem;
  border-radius: 999px;
  background: var(--danger);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
}

/* ── Inscriptions ─── */
.ins-verdict {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1rem 1.25rem;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: var(--bg-card);
  margin-bottom: 1rem;
}

.ins-verdict > i {
  font-size: 1.5rem;
  margin-top: 0.1rem;
}

.ins-verdict.is-ok { border-color: color-mix(in srgb, var(--success) 40%, var(--border)); }
.ins-verdict.is-ok > i { color: var(--success); }
.ins-verdict.is-warning { border-color: color-mix(in srgb, var(--warning) 50%, var(--border)); }
.ins-verdict.is-warning > i { color: var(--warning); }
.ins-verdict.is-critical { border-color: color-mix(in srgb, var(--danger) 50%, var(--border)); }
.ins-verdict.is-critical > i { color: var(--danger); }

.ins-verdict-title {
  font-weight: 600;
  font-size: 1rem;
}

.ins-verdict-sub {
  color: var(--text-muted);
  font-size: 0.82rem;
  margin-top: 0.2rem;
}

.ins-stats {
  flex-wrap: wrap;
  gap: 0.75rem;
}

.ins-stats .stat-card {
  flex: 1 1 120px;
}

.ins-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}

.ins-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.ins-filter {
  padding: 0.25rem 0.65rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: transparent;
  color: var(--text-muted);
  font-size: 0.78rem;
  cursor: pointer;
}

.ins-filter.active {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}

.ins-issues {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.ins-issue {
  border: 1px solid var(--border);
  border-left: 4px solid var(--text-muted);
  border-radius: 8px;
  padding: 0.7rem 0.9rem;
  background: var(--bg-input);
}

.ins-issue.sev-critical { border-left-color: var(--danger); }
.ins-issue.sev-warning { border-left-color: var(--warning); }

.ins-issue-top {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.ins-issue-date {
  margin-left: auto;
  font-size: 0.75rem;
  color: var(--text-muted);
  white-space: nowrap;
}

.ins-issue-title {
  font-size: 0.9rem;
  font-weight: 500;
  margin-top: 0.4rem;
}

.ins-issue-line {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
}

.ins-issue-detail,
.ins-attempt-detail {
  font-family: ui-monospace, "Cascadia Code", "SF Mono", Consolas, monospace;
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.3rem;
  word-break: break-word;
}

.ins-attempt-detail {
  max-width: 32ch;
}

.ins-issue-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
}

.ins-issue-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-left: auto;
}

.ins-acked {
  margin-top: 0.75rem;
  font-size: 0.78rem;
  color: var(--text-muted);
}

.ins-issue-hint {
  font-size: 0.78rem;
  color: var(--text-muted);
}

.table-count {
  margin-top: 0.75rem;
  color: var(--text-muted);
  font-size: 0.8rem;
}

/* ── Misc ─── */
.loader {
  color: var(--text-muted);
  font-size: 0.875rem;
  padding: 2rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.loader-sm {
  color: var(--text-muted);
  font-size: 0.875rem;
  padding: 0.5rem;
}

.empty {
  color: var(--text-muted);
  font-size: 0.875rem;
  padding: 1.5rem 0;
}

.mobile-cards {
  display: none;
}

.mobile-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

@media (max-width: 640px) {
  .table-wrapper {
    display: none;
  }

  .mobile-cards {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
}

@media (max-width: 768px) {
  .logs-layout {
    flex-direction: column;
  }

  .logs-sidebar {
    width: 100%;
  }

  .traffic-grid {
    grid-template-columns: 1fr;
  }

  .stats-row {
    flex-direction: column;
  }
}
</style>
